import React, { useState, useRef } from 'react';
import '../styles/home.css';
import { PDFDocument } from 'pdf-lib';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import imageToPdfIcon from '../assets/jpg-to-pdf.png';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';

const ImageToPdf = () => {
  const [visible, setVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [submitValue, setSubmitValue] = useState('Convert');
  const [errorMessage, setErrorMessage] = useState('');
  const [buttonVariant, setButtonVariant] = useState('disabled');
  const fileInputRef = useRef(null);

  /**
   * The `toggleModal` function toggles the visibility of a modal and resets certain state variables if
   * the modal is being hidden.
   */
  const toggleModal = () => {
    setVisible(!visible);
    if (!visible) {
      setFiles([]);
      setButtonVariant('disabled');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * The `handleFileChange` function adds selected files to an existing array and updates a button
   * variant to 'primary'.
   * @param event - The `event` parameter in the `handleFileChange` function represents the event that
   * occurred when a file input element's value changes, such as when a user selects files using a file
   * picker dialog.
   */
  const handleFileChange = (event) => {
    const selectedFiles = event.target.files;
    setFiles([...files, ...selectedFiles]);
    setButtonVariant('primary');
  };

  /**
   * The function `convertToPdf` converts selected image files to a PDF document and allows the user to
   * download the converted PDF.
   * @returns The `convertToPdf` function is returning either an error message if there is an issue
   * during the conversion process, or it is returning the converted PDF file for download.
   */
  const convertToPdf = async () => {
    if (files.length === 0) {
      setErrorMessage('Please select one or more image files.');
      return;
    }

    setSubmitValue('Processing...');
    try {
      const newPdfDoc = await PDFDocument.create();
      // Define the maximum width and height for the PDF pages
      const maxWidth = 612; // Standard PDF page width in points (8.5 inches)
      const maxHeight = 792; // Standard PDF page height in points (11 inches)

      for (const file of files) {
        const imageBytes = await file.arrayBuffer();
        let image;
        if (file.type === 'image/png') {
          image = await newPdfDoc.embedPng(imageBytes);
        } else if (file.type === 'image/jpeg') {
          image = await newPdfDoc.embedJpg(imageBytes);
        } else {
          setErrorMessage('Unsupported image format');
          continue;
        }

        const page = newPdfDoc.addPage([maxWidth, maxHeight]);
        const { width, height } = resizeToFit(image.width, image.height, maxWidth, maxHeight);
        const x = (maxWidth - width) / 2;
        const y = (maxHeight - height) / 2;
        page.drawImage(image, { x, y, width, height });
      }

      const pdfBytes = await newPdfDoc.save();
      const blob = new Blob([pdfBytes], { type: constants.pdfFileType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'converted.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);

      addUsage('AWESOME PDF TOOLS', '1.0', files.length, window.location.href);
      setSubmitValue('Convert');
      setVisible(false);
    } catch (error) {
      setSubmitValue('Convert');
      setErrorMessage('An error occurred while converting to PDF.');
    }
  };

  const resizeToFit = (width, height, maxWidth, maxHeight) => {
    const aspectRatio = width / height;

    // Calculate dimensions while preserving aspect ratio
    let newWidth = maxWidth;
    let newHeight = maxHeight;

    if (width / height > maxWidth / maxHeight) {
      newWidth = Math.min(width, maxWidth);
      newHeight = newWidth / aspectRatio;
    } else {
      newHeight = Math.min(height, maxHeight);
      newWidth = newHeight * aspectRatio;
    }
    return { width: newWidth, height: newHeight };
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        <div className="service-card-icon">
          <img src={imageToPdfIcon} alt="Image to PDF" loading="lazy" />
        </div>
        <h3 className="service-card-title">Image to PDF</h3>
        <p className="service-card-desc">Convert Image Files to PDF document online</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="small"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              <Button variant={buttonVariant} onClick={convertToPdf}>
                {submitValue}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Convert Image to PDF"
      >
        <FormField
          label="Upload image files"
          description="Select one or more image files to convert"
          errorText={errorMessage}
        >
          <input
            ref={fileInputRef}
            id="file-input"
            type="file"
            accept=".jpg,.jpeg,.png"
            multiple
            onChange={handleFileChange}
          />
        </FormField>
      </Modal>
    </div>
  );
};

export default ImageToPdf;
